import { ReactNode, useLayoutEffect, useRef } from "react"

export type AnimationStepDetail = {
  /** Seconds since prior step. */
  dt: number

  /** Seconds for which the current animation has been playing. */
  elapsed: number
}

export type AnimationStep = (detail: AnimationStepDetail) => void

export function Animation(props: {
  step: AnimationStep
}): ReactNode {
  const stepRef = useRef(props.step)
  stepRef.current = props.step

  // Ideally, instead of performance.now(), I'd use the time of the event that kicked off this animation. But that requires too much plumbing.
  let start = performance.now() / 1000.0
  let then = start

  useLayoutEffect(() => {
    let stopped = false

    function wrapper(_contextMilliseconds: number) {
      if (stopped) {
        return
      }

      // The value passed in as contextMilliseconds is weird. The TR that defined requestAnimationFrame says it is “the callback's context's *time*”. I couldn't find a definition of a “context's time”. This stackoverflow answer is helpful:
      //
      // https://stackoverflow.com/a/50313079/77567
      //
      // So in Chrome and Firefox, contextMilliseconds may be less than performance.now(). In my testing, it is less on the first callback. But the second callback receives a value about 1000/30 (two 60Hz frames) larger, and subsequent callbacks go up by about 1000/60 (one 60Hz frame) each. This makes it difficult to use. So I ignore it and just look at performance.now().

      const now = performance.now() / 1000.0
      const dt = now - then
      requestAnimationFrame(wrapper)

      // Sanity check.
      if (dt <= 0) {
        console.log(`timestamp insanity detected: start=${start.toFixed(6)} then=${then.toFixed(6)} now=${now.toFixed(6)} dt=${dt.toFixed(6)}`)
        return
      }

      then = now
      stepRef.current({ dt, elapsed: now - start })
    }

    requestAnimationFrame(wrapper)
    return () => {
      stopped = true
    }
  }, [])

  return undefined
}
